import type { ReopenHistoryEntry } from '../assessments/assessment.dto';
import { DisciplineApi } from '../../common/discipline.dto';
/**
 * Base care plan DTOs
 * Extracted from src/services/patientmr/careplans.service.ts - API contract types only
 */

/**
 * Care plan status enum — canonical API values only.
 * Legacy alias QA_APPROVED has been removed; the backend normalizes that
 * value to COMPLETE on read.
 */
export enum CarePlanStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  QA = 'QA',
  COMPLETE = 'COMPLETE',
  CURRENT = 'CURRENT',   // Reserved for Phase 2 — do not use in MVP
  EXPIRED = 'EXPIRED'    // Reserved for Phase 2 — do not use in MVP
}

/**
 * UI-facing CarePlan discipline enum
 */
// Phase 20A: registry disciplines
export const CarePlanDiscipline = DisciplineApi;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type CarePlanDiscipline = DisciplineApi;

/**
 * API-facing CarePlan discipline enum (matches backend CAREPLAN_DISCIPLINES: RN/PT/OT/ST/MSW/HHA)
 */
// Phase 20A: registry disciplines
export const ApiCarePlanDiscipline = DisciplineApi;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type ApiCarePlanDiscipline = DisciplineApi;

/**
 * Care plan list item for displaying in lists
 */
export type CarePlanListItem = {
  carePlanId: string;
  date: string;
  status: CarePlanStatus;
  discipline: CarePlanDiscipline;
  providerName: string;
};

/**
 * Care plan list response with pagination
 */
export type CarePlanListResponse = {
  careplans: CarePlanListItem[];
  paginationInfo?: {
    hasMoreItems: boolean;
    nextToken?: string;
  };
};

/**
 * Phase 19 F26: the certification period a care plan belongs to (from the patient's current
 * certification period when the plan was created). The plan shows these dates read-only.
 */
export type CarePlanCertificationPeriod = {
  startDate: string | null;
  endDate: string | null;
};

/**
 * Care plan detail entity
 * Contains discipline-specific care plan data
 */
export type CarePlanDetail = {
  carePlanId?: string;
  careplanType?: string;   // Root-level canonical value (INITIAL, FOLLOW_UP, DISCHARGE, SPECIALIZED)
  provider: string;
  discipline: CarePlanDiscipline;
  status?: CarePlanStatus;
  data: Record<string, any>;
  /** Record version; starts at 1 and goes up on each reopen (Phase 4) */
  version?: number;
  /** One row per reopen (Phase 4) */
  reopenHistory?: ReopenHistoryEntry[];
  /** Certification period record the plan belongs to (Phase 19 F9) */
  certificationPeriodId?: string | null;
  /** Dates of that period, or null for a legacy plan or a patient without a period (Phase 19 F26) */
  certificationPeriod?: CarePlanCertificationPeriod | null;
};
